// Typed RPC client over the SQLite worker. Holds one Worker instance and
// multiplexes requests by id; the React layer never touches postMessage.

// oxlint-disable-next-line import/default -- Vite's `?worker` suffix synthesizes a default export.
import SqliteWorker from '../worker/sqlite.worker?worker';
import type {
  DatabaseInfo,
  ExecResult,
  SchemaTable,
  SelectResult,
  WorkerInitInfo,
  WorkerRequest,
  WorkerResponse,
} from '../worker/protocol';

interface Pending {
  resolve: (data: unknown) => void;
  reject: (err: Error) => void;
}

// Distributive Omit so the resulting union still discriminates on `type`.
type RequestBody<T extends WorkerRequest = WorkerRequest> = T extends T ? Omit<T, 'id'> : never;

export class SqliteError extends Error {
  code?: string;
  constructor(message: string, code?: string) {
    super(message);
    this.code = code;
    this.name = 'SqliteError';
  }
}

export class SqliteClient {
  private worker: Worker;
  private pending = new Map<number, Pending>();
  private nextId = 1;
  private disposed = false;

  constructor() {
    // The SAH pool takes exclusive OPFS file locks, so an embedded YesQL (the
    // portfolio iframe is same-site and shares this origin's storage bucket)
    // and a direct tab would knock whichever loads second down to in-memory
    // storage. Tag the worker by context so it can pick a separate pool.
    const embedded = window.self !== window.top;
    this.worker = new SqliteWorker({ name: embedded ? 'yesql-embed' : 'yesql' });
    this.worker.onmessage = (ev: MessageEvent<WorkerResponse>) => {
      const res = ev.data;
      const p = this.pending.get(res.id);
      if (!p) return;
      this.pending.delete(res.id);
      if (res.ok) p.resolve(res.data);
      else p.reject(new SqliteError(res.error.message, res.error.code));
    };
    this.worker.onerror = (err) => {
      // Reject every outstanding request so the UI doesn't hang on a dead worker.
      const error = new SqliteError(err.message || 'SQLite worker crashed');
      for (const p of this.pending.values()) p.reject(error);
      this.pending.clear();
    };
  }

  private send<T>(req: RequestBody): Promise<T> {
    if (this.disposed) {
      return Promise.reject(new SqliteError('SQLite client is disposed'));
    }
    const id = this.nextId++;
    const message = { ...req, id } as WorkerRequest;
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, {
        resolve: (data) => resolve(data as T),
        reject,
      });
      this.worker.postMessage(message);
    });
  }

  init(): Promise<WorkerInitInfo> {
    return this.send<WorkerInitInfo>({ type: 'init' });
  }

  open(dbName: string): Promise<DatabaseInfo> {
    return this.send<DatabaseInfo>({ type: 'open', dbName });
  }

  close(): Promise<null> {
    return this.send<null>({ type: 'close' });
  }

  exec(sql: string): Promise<ExecResult> {
    return this.send<ExecResult>({ type: 'exec', sql });
  }

  select(sql: string, rowLimit?: number): Promise<SelectResult> {
    return this.send<SelectResult>({ type: 'select', sql, rowLimit });
  }

  schema(): Promise<SchemaTable[]> {
    return this.send<SchemaTable[]>({ type: 'schema' });
  }

  listDatabases(): Promise<DatabaseInfo[]> {
    return this.send<DatabaseInfo[]>({ type: 'listDatabases' });
  }

  deleteDatabase(dbName: string): Promise<null> {
    return this.send<null>({ type: 'deleteDatabase', dbName });
  }

  renameDatabase(from: string, to: string): Promise<null> {
    return this.send<null>({ type: 'renameDatabase', from, to });
  }

  exportDatabase(): Promise<{ name: string; bytes: ArrayBuffer }> {
    return this.send<{ name: string; bytes: ArrayBuffer }>({ type: 'export' });
  }

  importDatabase(dbName: string, bytes: ArrayBuffer): Promise<DatabaseInfo> {
    return this.send<DatabaseInfo>({ type: 'import', dbName, bytes });
  }

  dispose(): void {
    this.disposed = true;
    for (const p of this.pending.values()) {
      p.reject(new SqliteError('SQLite client disposed'));
    }
    this.pending.clear();
    this.worker.terminate();
  }
}
